/**
 * Presentation of values the operator has to read quickly and compare.
 *
 * Every formatter here is deterministic given its inputs and a timezone. None of
 * them reach for the browser's locale implicitly: a depot's numbers are read by
 * people in more than one place, and a figure that renders `1.234` in one
 * office and `1,234` in another is a support ticket waiting to happen.
 */

const NUMBER = new Intl.NumberFormat("en-GB");

const ENERGY = new Intl.NumberFormat("en-GB", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Watt-hours as kWh, which is the unit an operator thinks in. One decimal:
 * enough to distinguish two sessions, few enough to stay in a narrow column.
 */
export function kwh(wh: number | null | undefined): string {
  if (wh === null || wh === undefined) return "—";
  return `${ENERGY.format(wh / 1000)}`;
}

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return NUMBER.format(value);
}

export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${NUMBER.format(value)}%`;
}

/**
 * A timestamp in a stated zone. The zone is always passed in — usually the
 * depot's, because spec 6.1 draws period boundaries there, and a period that
 * starts at "17:00" in the viewer's browser starts at midnight where the
 * chargers are.
 */
export function timestamp(iso: string | null | undefined, timeZone: string): string {
  const date = parse(iso);
  if (date === undefined) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function dateOnly(iso: string | null | undefined, timeZone: string): string {
  const date = parse(iso);
  if (date === undefined) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
}

/**
 * "4m ago", for liveness. Coarse by design: the difference between 34 and 37
 * seconds is not a thing anyone acts on, and a display that rerenders every
 * second to show it costs more than it says.
 *
 * `now` is a parameter so this is testable without faking the clock.
 */
export function since(iso: string | null | undefined, now: number = Date.now()): string {
  const date = parse(iso);
  if (date === undefined) return "never";

  const seconds = Math.round((now - date.getTime()) / 1000);
  // A charge point whose clock runs fast produces a negative age. Reporting
  // "in 4m" would be honest but useless; the operator's question is only ever
  // "is this recent", and a skewed clock is still recent.
  if (seconds < 45) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;

  return `${Math.round(hours / 24)}d ago`;
}

/**
 * The same age, split so a `Metric` can set the figure large and the unit small.
 *
 * `since()` returns one string, which is right in a table cell and wrong at
 * display size — "9m ago" at 68px is a sentence pretending to be a number, and it
 * makes the digits in the metrics beside it stop lining up. Splitting keeps the
 * numeral in the numeric slot.
 */
export function sinceParts(
  iso: string | null | undefined,
  now: number = Date.now(),
): { readonly value: string; readonly unit: string | undefined } {
  const text = since(iso, now);

  const match = /^(\d+)([mhd]) ago$/.exec(text);
  if (match === null) {
    /**
     * "just now" and "never" have no numeral to separate out, so the word goes in
     * the value slot and the unit slot stays empty.
     *
     * An em dash was there before, which set a 68px horizontal bar in the metric
     * and read as a rendering fault rather than as "this station has never sent a
     * heartbeat". A word is not a numeral, but it is the honest answer, and it is
     * only reached in the two cases where no figure exists.
     */
    return { value: text === "just now" ? "now" : text, unit: undefined };
  }

  return { value: match[1] ?? text, unit: `${match[2]} ago` };
}

/**
 * A heartbeat older than this reads as stale even though the row still holds a
 * timestamp. Spec 2.4's default interval is 300s; three missed beats is the
 * point at which a station has stopped talking rather than been slow.
 */
const STALE_HEARTBEAT_MS = 15 * 60 * 1000;

export function isHeartbeatStale(iso: string | null, now: number = Date.now()): boolean {
  const date = parse(iso);
  if (date === undefined) return true;

  return now - date.getTime() > STALE_HEARTBEAT_MS;
}

function parse(iso: string | null | undefined): Date | undefined {
  if (iso === null || iso === undefined || iso === "") return undefined;

  const date = new Date(iso);
  // `new Date("nonsense")` yields an Invalid Date rather than throwing, and
  // formatting one produces the literal string "Invalid Date" in the middle of
  // a table. An em dash is the honest answer.
  return Number.isNaN(date.getTime()) ? undefined : date;
}
